import React from 'react';

import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import SanityBlockRenderer from 'components/sanity-block-renderer';
import { formatDate, t } from 'state/intl/translations';
import { primitive } from 'styles/constants';

import { BadgeProgressBar } from './badge-progress-bar';
import { LoyaltyBadgeImage } from './loyalty-badge-image';
import { ILoyaltyBadge } from './types';

export interface LoyaltyBadgeDetailsContentProps {
  badge: ILoyaltyBadge;
  showCelebration: boolean;
}

export const LoyaltyBadgeDetailsContent = ({
  badge,
  showCelebration,
}: LoyaltyBadgeDetailsContentProps) => (
  <>
    <View style={styles.outerContent}>
      <LoyaltyBadgeImage
        badge={badge}
        style={styles.badgeImage}
        showCelebration={showCelebration}
      />

      <View style={styles.innerContent} accessible>
        <Typography variant="headerOne" accessibilityLevel={2} style={styles.header} accessible>
          {badge.name}
        </Typography>
        {!!badge.description && (
          <SanityBlockRenderer
            content={badge.description}
            textProps={{ style: styles.description, focusable: true, accessible: true }}
          />
        )}
        {!badge.earnedAt && !!badge.progress && badge.progress.currentValue > 0 && (
          <BadgeProgressBar progress={badge.progress} variant="detail" />
        )}
      </View>
    </View>
    {badge.earnedAt && (
      <Typography style={styles.earnedAtMessage} accessible>
        {t('achievedOnDate', {
          date: formatDate(badge.earnedAt),
          defaultValue: 'Achieved on {date}',
        })}
      </Typography>
    )}
    {!!badge.terms && (
      <View style={styles.termsContainer}>
        <Typography bold style={styles.termsHeader}>
          {t('terms', 'Terms')}
        </Typography>
        <SanityBlockRenderer content={badge.terms} textProps={{ style: styles.termsBody }} />
      </View>
    )}
  </>
);

const styles = StyleSheet.create({
  outerContent: {
    alignItems: 'center',
    gap: 24,
  },
  innerContent: {
    width: '100%',
    gap: 16,
  },
  header: {
    marginBottom: 0,
    textAlign: 'center',
    fontSize: primitive.fontSizes['2xl'],
  },
  badgeImage: {
    width: 200,
    height: 200,
  },
  description: {
    fontSize: primitive.fontSizes.lg,
    textAlign: 'center',
  },
  earnedAtMessage: {
    fontSize: primitive.fontSizes.sm,
    textAlign: 'center',
  },
  termsContainer: {
    gap: 8,
  },
  termsHeader: {
    fontSize: primitive.fontSizes.sm,
    color: primitive.$bbqBrown,
  },
  termsBody: {
    fontSize: primitive.fontSizes.xs,
  },
});
